import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contractApi } from "../../../../modules/contract/api";
import { CreateUpdateContractIncrement } from "../../../../modules/contract/types";
import { EntityObject, PageRequest, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../types";
import { CONTRACTS_QUERY_KEYS } from "./contracts";

export const CONTRACT_INCREMENTS_QUERY_KEYS = {
  filterContractIncrements: (payload?: EntityObject<PageRequest>) =>
    payload ? ["filterContractIncrements", payload] : ["filterContractIncrements"],
  createContractIncrement: () => ["createContractIncrement"],
  updateContractIncrement: () => ["updateContractIncrement"],
  deleteContractIncrement: () => ["deleteContractIncrement"],
};

export const useFilterContractIncrements = (payload: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: CONTRACT_INCREMENTS_QUERY_KEYS.filterContractIncrements(payload),
    queryFn: async () => {
      const { data } = await contractApi.filterContractIncrements(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateContractIncrement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACT_INCREMENTS_QUERY_KEYS.createContractIncrement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateContractIncrement>) => {
      const { data } = await contractApi.createContractIncrement(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACT_INCREMENTS_QUERY_KEYS.filterContractIncrements() });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id) });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateContractIncrement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACT_INCREMENTS_QUERY_KEYS.updateContractIncrement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateContractIncrement>) => {
      const { data } = await contractApi.updateContractIncrement(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACT_INCREMENTS_QUERY_KEYS.filterContractIncrements() });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id1) });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteContractIncrement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACT_INCREMENTS_QUERY_KEYS.deleteContractIncrement(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return contractApi.deleteContractIncrement(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACT_INCREMENTS_QUERY_KEYS.filterContractIncrements() });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id1) });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
      Notification.itemDeletedNotification();
    },
  });
};
